// Keys (DESIGN §5). T or 1 tap · Space play/pause · ← → one 8-count · [ ] move the 1 a beat · H half beat · F flip 1↔5 · L loop · C cues ·
// B big mode · ? help (and Z undo, U undo tap, R review taps, Y yes: the automatic count is right). Controls reachable by Tab keep their own Space and Enter: a key the focus ring is on belongs to it.

import { useEffect } from 'preact/hooks';
import { engine } from '../engine/engine.js';
import { answer, cycleCues, fix, reviewTaps, seekPhrase, tapStage, toggleLoop, undo, undoTap } from './actions.js';
import { calibration } from './calibrate-run.js';
import { closeSheet, openSheet, toggleBig, ui } from './ui.js';

const TEXT_INPUTS = new Set(['text', 'search', 'number', 'email', 'password', 'url', 'tel']);

export function useKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target instanceof HTMLElement ? e.target : null;
      if (el && (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || (el instanceof HTMLInputElement && TEXT_INPUTS.has(el.type)))) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const { screen, sheet, big } = ui.get();
      const keyboardOnButton = !!el && el.matches('button, [role="switch"], [role="radio"], input[type="radio"], input[type="checkbox"], a[href]') && el.matches(':focus-visible');

      if (key === '?') {
        e.preventDefault();
        if (sheet === 'help') closeSheet(); else if (sheet === null) openSheet('help');
        return;
      }
      if (calibration.get().running) {
        if (key === 't' || key === '1' || (key === ' ' && !keyboardOnButton)) { e.preventDefault(); if (!e.repeat) tapStage(); }
        return;
      }
      if (sheet !== null || screen !== 'play' || engine.getState().source === 'none') return;
      if (key === 'Escape') { if (big) toggleBig(); return; }
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        if (el instanceof HTMLInputElement && el.type === 'range') return; // a slider keeps its arrows
        e.preventDefault();
        seekPhrase(key === 'ArrowLeft' ? -1 : 1);
        return;
      }
      if (e.repeat) return;
      switch (key) {
        case 't': case '1': e.preventDefault(); tapStage(); break;
        case ' ':
          if (keyboardOnButton) return;
          e.preventDefault();
          void engine.toggle();
          break;
        case '[': fix('earlier'); break;
        case ']': fix('later'); break;
        case 'h': fix('half'); break;
        case 'f': fix('flip'); break;
        case 'l': toggleLoop(); break;
        case 'c': cycleCues(); break;
        case 'b': toggleBig(); break;
        case 'z': undo(); break;
        case 'u': undoTap(); break;
        case 'r': reviewTaps(); break;
        case 'y': answer('yes'); break;
        default: return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
